import { useEffect, useState } from 'react'
import {
  detectAudioContainerFromBytes,
  ensureAudioBlobType,
  getAudioContainerFromMimeType,
  normalizeRecordingBlob,
} from '@/lib/audio'

interface UseBlobPreparationInput {
  blobSource: Blob | null | undefined
  hasChunkSource: boolean
  isRemoteAudio: boolean
  isDraft: boolean
  recordingId: string
}

interface UseBlobPreparationResult {
  playbackBlob: Blob | null
  playbackSupported: boolean
  setPlaybackBlob: (blob: Blob | null) => void
  setPlaybackSupported: (supported: boolean) => void
}

// Validates the recording's blob (draft or submission localBlob), detects its
// container, normalizes unsupported formats to WAV, and exposes the resulting
// playback-ready blob plus a browser-support flag.
export function useBlobPreparation({
  blobSource,
  hasChunkSource,
  isRemoteAudio,
  isDraft,
  recordingId,
}: UseBlobPreparationInput): UseBlobPreparationResult {
  const [playbackBlob, setPlaybackBlob] = useState<Blob | null>(null)
  const [playbackSupported, setPlaybackSupported] = useState(true)

  useEffect(() => {
    let cancelled = false

    const preparePlayback = async (): Promise<void> => {
      console.log('[TESTING] preparePlayback started', {
        hasBlobSource: !!blobSource,
        blobSourceType: blobSource?.type,
        blobSourceSize: blobSource?.size,
        isDraft,
        recordingId,
      })

      if (!blobSource) {
        console.log('[TESTING] No blob source available')
        if (!cancelled) {
          setPlaybackBlob(null)
          // Remote (S3) playback needs no local blob; keep the player enabled.
          setPlaybackSupported(hasChunkSource || isRemoteAudio)
        }
        return
      }

      console.log('[STEP-BY-STEP] Step 1: Validating blob from IndexedDB', {
        recordingId,
        isDraft,
        blobSize: blobSource.size,
        blobType: blobSource.type,
        blobConstructor: blobSource.constructor.name,
      })

      if (blobSource.size === 0) {
        console.warn('[STEP-BY-STEP] Blob has zero size, falling back to chunk source', {
          recordingId,
          blobType: blobSource.type,
          hasChunkSource,
        })
        if (!cancelled) {
          setPlaybackBlob(null)
          setPlaybackSupported(hasChunkSource || isRemoteAudio)
        }
        return
      }

      let canReadBlob = false
      let blobArrayBuffer: ArrayBuffer | null = null
      try {
        console.log('[STEP-BY-STEP] Step 2: Attempting to read blob as ArrayBuffer...')
        blobArrayBuffer = await blobSource.arrayBuffer()
        canReadBlob = blobArrayBuffer.byteLength > 0
        console.log('[STEP-BY-STEP] Step 2 Result: Blob read successful', {
          arrayBufferSize: blobArrayBuffer.byteLength,
          matchesBlobSize: blobArrayBuffer.byteLength === blobSource.size,
          canReadBlob,
        })

        if (!canReadBlob) {
          console.error('[STEP-BY-STEP] ERROR: Blob ArrayBuffer is empty after read!', {
            recordingId,
            blobSize: blobSource.size,
            arrayBufferSize: blobArrayBuffer.byteLength,
          })
          if (!cancelled) {
            setPlaybackBlob(null)
            setPlaybackSupported(false)
          }
          return
        }
      } catch (readError) {
        console.error('[STEP-BY-STEP] ERROR: Failed to read blob as ArrayBuffer', {
          recordingId,
          error: readError instanceof Error ? readError.message : String(readError),
          blobSize: blobSource.size,
          blobType: blobSource.type,
        })
        if (!cancelled) {
          setPlaybackBlob(null)
          setPlaybackSupported(false)
        }
        return
      }

      try {
        console.log('[STEP-BY-STEP] Step 3: Reading blob header bytes...')
        const firstBytes = blobArrayBuffer!.slice(0, 12)
        const headerBytes = new Uint8Array(firstBytes)
        const headerString = String.fromCharCode(...headerBytes.slice(0, 4))
        const headerHex = Array.from(headerBytes).map(b => b.toString(16).padStart(2, '0')).join(' ')
        const declaredContainer = getAudioContainerFromMimeType(blobSource.type)
        const detectedContainer = detectAudioContainerFromBytes(headerBytes)
        const hasContainerMismatch =
          declaredContainer !== 'unknown' &&
          detectedContainer !== 'unknown' &&
          declaredContainer !== detectedContainer

        console.log('[STEP-BY-STEP] Step 3 Result: Blob header analysis', {
          headerString,
          headerHex,
          blobSize: blobSource.size,
          blobType: blobSource.type,
          declaredContainer,
          detectedContainer,
          hasContainerMismatch,
        })

        if (declaredContainer === 'wav' && detectedContainer !== 'unknown' && detectedContainer !== 'wav') {
          console.warn('[STEP-BY-STEP] WARN: Declared WAV blob has mismatched container', {
            recordingId,
            expectedContainer: 'wav',
            declaredContainer,
            detectedContainer,
            declaredType: blobSource.type,
            headerHex,
          })
        }
      } catch (headerError) {
        console.error('[STEP-BY-STEP] ERROR: Failed to read blob header', {
          recordingId,
          error: headerError instanceof Error ? headerError.message : String(headerError),
        })
      }

      const tester = document.createElement('audio')
      const canPlay = (candidate: Blob): boolean => {
        const type = candidate.type || 'audio/webm'
        const support = tester.canPlayType(type)
        console.log('[TESTING] canPlayType check', { type, support, result: support !== '' })
        return support === 'probably' || support === 'maybe'
      }

      console.log('[STEP-BY-STEP] Step 4: Ensuring blob has MIME type...')
      let candidate = ensureAudioBlobType(blobSource)
      console.log('[STEP-BY-STEP] Step 4 Result: Blob type ensured', {
        originalType: blobSource.type,
        candidateType: candidate.type,
        candidateSize: candidate.size,
        typeChanged: blobSource.type !== candidate.type,
      })

      console.log('[STEP-BY-STEP] Step 5: Checking browser playback support...')
      let supported = canPlay(candidate)
      console.log('[STEP-BY-STEP] Step 5 Result: Playback support check', {
        blobType: candidate.type,
        supported,
      })

      const blobType = candidate.type?.toLowerCase() || ''
      const isUnsupportedFormat = !blobType || blobType === 'application/octet-stream'

      console.log('[STEP-BY-STEP] Step 6: Format compatibility analysis', {
        blobType,
        isUnsupportedFormat,
        supported,
        needsConversion: !supported || isUnsupportedFormat,
      })

      if (!supported || isUnsupportedFormat) {
        console.log('[STEP-BY-STEP] Step 7: Converting to WAV format...', {
          reason: !supported ? 'not supported by browser' : 'unsupported format',
          candidateType: candidate.type,
          candidateSize: candidate.size,
        })
        try {
          const normalized = await normalizeRecordingBlob(candidate)
          console.log('[STEP-BY-STEP] Step 7 Result: WAV conversion successful', {
            normalizedType: normalized.blob.type,
            normalizedSize: normalized.blob.size,
            extension: normalized.extension,
            sizeChange: `${candidate.size} → ${normalized.blob.size}`,
          })
          candidate = normalized.blob
          supported = canPlay(candidate)
          console.log('[STEP-BY-STEP] Step 7 Final: Playback support after conversion', {
            supported,
            finalType: candidate.type,
            finalSize: candidate.size,
          })
        } catch (conversionError) {
          console.error('[STEP-BY-STEP] ERROR: Failed to convert audio to WAV', {
            recordingId,
            error: conversionError instanceof Error ? conversionError.message : String(conversionError),
            candidateType: candidate.type,
            candidateSize: candidate.size,
          })
          supported = canPlay(candidate)
        }
      }

      if (cancelled) {
        console.log('[TESTING] Cancelled during preparation')
        return
      }

      console.log('[STEP-BY-STEP] Step 8: Final blob validation...')
      try {
        const finalHeader = await candidate.slice(0, 12).arrayBuffer()
        const finalHeaderBytes = new Uint8Array(finalHeader)
        const headerByteArray = Array.from(finalHeaderBytes)
        const headerString = String.fromCharCode(...headerByteArray.slice(0, 4))
        const headerHex = headerByteArray.map(b => b.toString(16).padStart(2, '0')).join(' ')
        const declaredContainer = getAudioContainerFromMimeType(candidate.type)
        const detectedContainer = detectAudioContainerFromBytes(finalHeaderBytes)
        const hasContainerMismatch =
          declaredContainer !== 'unknown' &&
          detectedContainer !== 'unknown' &&
          declaredContainer !== detectedContainer
        console.log('[STEP-BY-STEP] Step 8 Result: Final blob validation complete', {
          headerString,
          headerBytes: headerByteArray,
          headerHex,
          size: candidate.size,
          type: candidate.type,
          supported,
          declaredContainer,
          detectedContainer,
          hasContainerMismatch,
        })
      } catch (validationError) {
        console.error('[STEP-BY-STEP] ERROR: Failed to validate final blob', {
          recordingId,
          error: validationError instanceof Error ? validationError.message : String(validationError),
          candidateSize: candidate.size,
          candidateType: candidate.type,
        })
      }

      setPlaybackSupported(supported)
      if (!supported) {
        console.log('[TESTING] Format not supported, setting playbackBlob to null')
        setPlaybackBlob(null)
        return
      }

      console.log('[TESTING] Setting playbackBlob', {
        type: candidate.type,
        size: candidate.size,
        supported,
      })
      setPlaybackBlob(candidate)
    }

    void preparePlayback()

    return () => {
      cancelled = true
    }
  }, [blobSource, hasChunkSource, isRemoteAudio, isDraft, recordingId])

  return { playbackBlob, playbackSupported, setPlaybackBlob, setPlaybackSupported }
}
